// My agent (`/c/{slug}/agents/me`, opened from the account menu — UX pass 1.1 took it off the
// Workforce sidebar; M4-01, M4-06, 05 §Personal crews; Agents UX pass 2026-10-21): the signed-in person's own crew, from `GET crews/me`. A 56 px location header
// (name, state, Pause / Resume), then settings sections, each saving on its own:
// - Profile: the agent's name (`PATCH crews/me`);
// - Crew (M4-06): the crew's agents, add one with skill packs within my role's policy, remove
//   mine (crew-agents.tsx);
// - Budget (M4-06): spent against the cap that applies; lower my own cap (crew-agents.tsx);
// - Schedule, Reminders, Role memory (M4-03, crew-features.tsx); Mail and calendar (M4-04,
//   crew-connections.tsx);
// - Autonomy: per action kind, alone / ask / never and as-agent / in-my-name. `alone` only where
//   a role skill policy allows it (`alone_allowed`); the whole set is PUT;
// - Sharing: what the agent tells other people's agents, per subject kind; the whole set is PUT;
// - Interview: the seven questions, resumable ("Save answers"); "Save and finish" writes the
//   answers into the context layers;
// - Knowledge (DOCS-KB): the docs, spaces and page kinds it reads (features/agents/knowledge-tab);
// - Context: the current layers, read-only;
// - Hire as a DE (M7-05, managers; hire-as-de.tsx).
// The screen loads crews.me, the reminders and (DOCS-KB) the crew's knowledge (role memory, mail
// and calendar, the crew's skill packs and the hire form read when opened), beside the Agents
// sidebar's own call. A 404 means the crew is still being set up. Refusals show inline and as
// the shell's toast. With the `agents` add-on off the screen is the add-on banner alone (no
// call, no sample of what it holds); no "not enabled" page.
import type { OperationOutput } from "@companyos/sdk";
import {
  Badge,
  Button,
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
  Input,
  LocationHeader,
  NativeSelect,
  NativeSelectOption,
  NavList,
  NavRow,
  Notice,
  SaveIndicator,
  SettingsLayout,
  SettingsSection,
  Textarea,
  workerName,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  BookOpenIcon,
  BotIcon,
  LibraryIcon,
  MessageSquareTextIcon,
  PauseIcon,
  PlayIcon,
  ShareIcon,
  SlidersIcon,
  UserRoundIcon,
} from "lucide-react";
import { useState } from "react";
import type { Api } from "../../api/client";
import { keepFocus } from "../../focus/focus";
import { AgentsDoc, AgentsOffBanner } from "../agents/agents-ux";
import { KnowledgeTab } from "../agents/knowledge-tab";
import { type CompanyAccess, isManager } from "../company";
import { PersonalMemoryConsent } from "../memory/personal-consent";
import { crewKeys, myCrewQuery } from "../queries";
import { LoadError, Loading, Nothing } from "../screen-parts";
import { problemText } from "../settings/problem-text";
import { useFeature } from "../use-feature";
import { CrewAgentsSection, CrewBudgetSection } from "./crew-agents";
import { MailCalendarSection } from "./crew-connections";
import { BriefSwitch, MemorySection, RemindersSection, ScheduleSection } from "./crew-features";
import { HireAsDeSection } from "./hire-as-de";

type CrewView = OperationOutput<"crews.me">;
type Autonomy = CrewView["autonomy"][number];
type Sharing = CrewView["sharing"][number];

export const ACTION_LABELS: Record<Autonomy["action_kind"], string> = {
  message: "Send messages",
  task_change: "Change tasks",
  page: "Edit pages",
  external_action: "Act outside CompanyOS",
  delegation: "Delegate work",
  reminder: "Set reminders",
  inbox_triage: "Triage my inbox",
  // CALENDAR-C1: create and move events on my own calendar.
  calendar: "Change my calendar",
};
const MODE_LABELS: Record<Autonomy["mode"], string> = {
  alone: "On its own",
  ask: "Ask me first",
  never: "Never",
};
const IDENTITY_LABELS: Record<Autonomy["identity_mode"], string> = {
  as_agent: "As my agent",
  on_behalf_of: "In my name",
};
export const SUBJECT_LABELS: Record<Sharing["subject_kind"], string> = {
  task: "Tasks",
  message: "Messages",
  dm: "Direct messages",
  calendar: "Calendar",
  memory: "Memory",
  run: "Runs",
};
const RULE_LABELS: Record<Sharing["rule"], string> = {
  never: "Never",
  status_only: "Status only",
  if_requester_can_see: "If they can see it",
  always: "Always",
};
const PAUSE_REASONS: Record<NonNullable<CrewView["crew"]["pause_reason"]>, string> = {
  owner: "You paused it.",
  budget: "Its spending cap was reached for this period.",
  membership: "Your membership is paused.",
};
const STATE_LABELS: Record<CrewView["crew"]["state"], string> = {
  active: "Active",
  paused: "Paused",
  deactivated: "Deactivated",
};
const capitalise = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** The sections, in page order, for the "On this page" list. */
const SECTIONS: { id: string; label: string; managers?: boolean }[] = [
  { id: "profile", label: "Profile" },
  { id: "crew", label: "Crew" },
  { id: "budget", label: "Budget" },
  { id: "schedule", label: "Schedule" },
  { id: "reminders", label: "Reminders" },
  { id: "role-memory", label: "Role memory" },
  { id: "personal-memory", label: "Personal memory" },
  { id: "mail-calendar", label: "Mail and calendar" },
  { id: "autonomy", label: "Autonomy" },
  { id: "sharing", label: "Sharing" },
  { id: "interview", label: "Interview" },
  { id: "knowledge", label: "Knowledge" },
  { id: "context", label: "Context" },
  { id: "hire-as-de", label: "Hire as a DE", managers: true },
];

export function MyAgentScreen({ api, company }: { api: Api; company: CompanyAccess }) {
  const agents = useFeature("agents");
  const crew = useQuery({ ...myCrewQuery(api, company.id), enabled: agents.enabled });
  const co = company.id;
  const toggle = useCrewSave(co, (state: "pause" | "resume") =>
    state === "pause"
      ? api.client.crews.pause({ params: { co } })
      : api.client.crews.resume({ params: { co } }),
  );
  const view = crew.data ?? null;
  const state = view?.crew.state;
  return (
    <div className="flex min-w-0 flex-1 flex-col" data-slot="my-agent">
      <LocationHeader
        crumbs={[
          {
            // UX-B: the mode's name ("Agents" means the AGs); opened from the account menu.
            label: "Team",
            render: (
              <Link to="/c/$slug/agents" params={{ slug: company.slug }} search={keepFocus} />
            ),
          },
        ]}
        icon={UserRoundIcon}
        title={view ? workerName(view.agent.display_name) : "My agent"}
        badge={
          view ? (
            <span className="inline-flex items-center gap-2" data-slot="crew-state">
              <Badge variant={state === "active" ? "success" : "warning"} dot>
                {STATE_LABELS[view.crew.state]}
              </Badge>
              {state === "paused" && view.crew.pause_reason ? (
                <span className="text-xs text-muted-foreground">
                  {PAUSE_REASONS[view.crew.pause_reason]}
                </span>
              ) : null}
              {/* COS-SLICE1: the daily brief, switched on in one click. */}
              <BriefSwitch api={api} company={company} view={view} />
            </span>
          ) : null
        }
        actions={
          view && state === "active" ? (
            <Button
              variant="secondary"
              size="sm"
              disabled={toggle.mutation.isPending}
              onClick={() => toggle.mutation.mutate("pause")}
            >
              <PauseIcon aria-hidden="true" />
              Pause
            </Button>
          ) : view && state === "paused" ? (
            <Button
              variant="secondary"
              size="sm"
              disabled={toggle.mutation.isPending}
              onClick={() => toggle.mutation.mutate("resume")}
            >
              <PlayIcon aria-hidden="true" />
              Resume
            </Button>
          ) : null
        }
      />
      {agents.enabled ? null : <AgentsOffBanner company={company} />}
      <AgentsDoc>
        {toggle.mutation.error ? (
          <ErrorLine error={toggle.mutation.error} fallback="Your agent could not be changed." />
        ) : null}
        {!agents.enabled ? null : crew.isPending ? (
          <Loading label="Loading your agent" rows={6} />
        ) : crew.isError ? (
          <LoadError what="Your agent" error={crew.error} />
        ) : crew.data === null ? (
          <Nothing title="Your agent is being set up.">
            It is created for every person of {company.name}; check back in a minute.
          </Nothing>
        ) : (
          // Each section keeps what is typed until it is saved; one key per crew.
          <MyAgent
            key={crew.data.crew.membership_id}
            api={api}
            company={company}
            view={crew.data}
          />
        )}
      </AgentsDoc>
    </div>
  );
}

/** Saves through `call` and puts the returned view in the cache. */
function useCrewSave<T>(co: string, call: (input: T) => Promise<CrewView>) {
  const qc = useQueryClient();
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const mutation = useMutation({
    mutationFn: call,
    onSuccess: (view) => {
      qc.setQueryData(crewKeys.me(co), view);
      setSavedAt(Date.now());
    },
  });
  return { mutation, savedAt };
}

function MyAgent({ api, company, view }: { api: Api; company: CompanyAccess; view: CrewView }) {
  const co = company.id;
  const manager = isManager(company);
  return (
    <SettingsLayout
      aside={
        <nav aria-label="On this page" data-slot="my-agent-nav" className="max-lg:hidden">
          <NavList>
            {SECTIONS.filter((s) => !s.managers || manager).map((s) => (
              <NavRow key={s.id} label={s.label} render={<a href={`#${s.id}`} />} />
            ))}
          </NavList>
        </nav>
      }
    >
      <ProfileSection api={api} co={co} view={view} />
      <CrewAgentsSection api={api} company={company} view={view} />
      <CrewBudgetSection api={api} co={co} view={view} />
      <ScheduleSection api={api} company={company} view={view} />
      <RemindersSection api={api} company={company} />
      <MemorySection api={api} company={company} />
      {/* D12: whether my agent may read my personal memory (read only, mine to give) */}
      <PersonalMemoryConsent api={api} co={co} />
      <MailCalendarSection api={api} company={company} />
      <AutonomySection api={api} co={co} view={view} />
      <SharingSection api={api} co={co} view={view} />
      <InterviewSection api={api} co={co} view={view} />
      <SettingsSection
        id="knowledge"
        icon={LibraryIcon}
        title="Knowledge"
        description="The docs your agent reads when it works."
      >
        <KnowledgeTab
          api={api}
          company={company}
          membershipId={view.crew.membership_id}
          agentName={workerName(view.agent.display_name)}
        />
      </SettingsSection>
      <ContextSection view={view} />
      {manager ? <HireAsDeSection api={api} company={company} view={view} /> : null}
    </SettingsLayout>
  );
}

type SectionProps = { api: Api; co: string; view: CrewView };

function ErrorLine({ error, fallback }: { error: unknown; fallback: string }) {
  return error ? (
    <Notice tone="danger" role="alert" className="mb-4">
      {problemText(error, fallback)}
    </Notice>
  ) : null;
}

function ProfileSection({ api, co, view }: SectionProps) {
  const [name, setName] = useState(view.agent.display_name);
  const rename = useCrewSave(co, (display_name: string) =>
    api.client.crews.update({ params: { co }, body: { display_name } }),
  );
  const empty = !name.trim();
  const dirty = name.trim() !== view.agent.display_name;
  return (
    <SettingsSection id="profile" icon={BotIcon} title="Profile">
      <form
        aria-label="Agent profile"
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (!empty && dirty) rename.mutation.mutate(name.trim());
        }}
      >
        <Field data-invalid={empty || undefined} className="max-w-md">
          <FieldLabel htmlFor="crew-name">Agent name</FieldLabel>
          <Input
            id="crew-name"
            className="h-10"
            value={name}
            maxLength={200}
            aria-invalid={empty || undefined}
            onChange={(e) => setName(e.target.value)}
          />
          {empty ? <FieldError>Your agent needs a name.</FieldError> : null}
        </Field>
        <div className="flex items-center gap-3">
          <Button type="submit" size="sm" disabled={empty || !dirty || rename.mutation.isPending}>
            {rename.mutation.isPending ? "Saving…" : "Save name"}
          </Button>
          <SaveIndicator savedAt={rename.savedAt} />
        </div>
        <ErrorLine error={rename.mutation.error} fallback="The name could not be saved." />
      </form>
    </SettingsSection>
  );
}

function AutonomySection({ api, co, view }: SectionProps) {
  const [rows, setRows] = useState<Autonomy[]>(view.autonomy);
  const save = useCrewSave(co, (policies: Autonomy[]) =>
    api.client.crews.putAutonomy({
      params: { co },
      body: {
        policies: policies.map((p) => ({
          action_kind: p.action_kind,
          mode: p.mode,
          identity_mode: p.identity_mode,
          constraints: p.constraints,
        })),
      },
    }),
  );
  const set = (kind: Autonomy["action_kind"], patch: Partial<Autonomy>) =>
    setRows((rs) => rs.map((r) => (r.action_kind === kind ? { ...r, ...patch } : r)));
  const dirty = JSON.stringify(rows) !== JSON.stringify(view.autonomy);
  return (
    <SettingsSection id="autonomy" icon={SlidersIcon} title="Autonomy">
      <form
        aria-label="Autonomy"
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutation.mutate(rows, { onSuccess: (v) => setRows(v.autonomy) });
        }}
      >
        <div className="flex flex-col divide-y divide-border-subtle">
          <div className="grid grid-cols-[minmax(0,1fr)_11rem_10rem] gap-3 pb-1 text-xs text-muted-foreground max-md:hidden">
            <span>Action</span>
            <span>May it act?</span>
            <span>Acts</span>
          </div>
          {rows.map((r) => {
            const label = ACTION_LABELS[r.action_kind];
            return (
              <div
                key={r.action_kind}
                data-action-kind={r.action_kind}
                className="grid grid-cols-[minmax(0,1fr)_11rem_10rem] items-center gap-3 py-2 max-md:grid-cols-1"
              >
                <span className="text-sm">
                  {label}
                  {r.alone_allowed ? null : (
                    <span className="block text-xs text-muted-foreground">
                      Not allowed on its own by your role.
                    </span>
                  )}
                </span>
                <NativeSelect
                  aria-label={`${label}: autonomy`}
                  className="w-full"
                  value={r.mode}
                  onChange={(e) => {
                    const mode = e.target.value as Autonomy["mode"];
                    set(r.action_kind, {
                      mode,
                      ...(mode === "never" ? { identity_mode: "as_agent" as const } : {}),
                    });
                  }}
                >
                  {(Object.keys(MODE_LABELS) as Autonomy["mode"][]).map((m) => (
                    <NativeSelectOption
                      key={m}
                      value={m}
                      disabled={m === "alone" && !r.alone_allowed}
                    >
                      {MODE_LABELS[m]}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
                <NativeSelect
                  aria-label={`${label}: acts`}
                  className="w-full"
                  value={r.identity_mode}
                  onChange={(e) =>
                    set(r.action_kind, {
                      identity_mode: e.target.value as Autonomy["identity_mode"],
                    })
                  }
                >
                  {(Object.keys(IDENTITY_LABELS) as Autonomy["identity_mode"][]).map((m) => (
                    <NativeSelectOption
                      key={m}
                      value={m}
                      disabled={m === "on_behalf_of" && r.mode === "never"}
                    >
                      {IDENTITY_LABELS[m]}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
              </div>
            );
          })}
        </div>
        <div className="flex items-center gap-3">
          <Button type="submit" size="sm" disabled={!dirty || save.mutation.isPending}>
            {save.mutation.isPending ? "Saving…" : "Save autonomy"}
          </Button>
          <SaveIndicator savedAt={save.savedAt} />
        </div>
        <ErrorLine error={save.mutation.error} fallback="Autonomy could not be saved." />
      </form>
    </SettingsSection>
  );
}

function SharingSection({ api, co, view }: SectionProps) {
  const [rows, setRows] = useState<Sharing[]>(view.sharing);
  const save = useCrewSave(co, (rules: Sharing[]) =>
    api.client.crews.putSharing({
      params: { co },
      body: { rules: rules.map((r) => ({ subject_kind: r.subject_kind, rule: r.rule })) },
    }),
  );
  const dirty = JSON.stringify(rows) !== JSON.stringify(view.sharing);
  return (
    <SettingsSection id="sharing" icon={ShareIcon} title="Sharing">
      <form
        aria-label="Sharing"
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutation.mutate(rows, { onSuccess: (v) => setRows(v.sharing) });
        }}
      >
        <div className="flex flex-col divide-y divide-border-subtle">
          <div className="grid grid-cols-[minmax(0,1fr)_14rem] gap-3 pb-1 text-xs text-muted-foreground max-md:hidden">
            <span>What other people's agents ask about</span>
            <span>Told</span>
          </div>
          {rows.map((r) => {
            const label = SUBJECT_LABELS[r.subject_kind];
            return (
              <div
                key={r.subject_kind}
                data-subject-kind={r.subject_kind}
                className="grid grid-cols-[minmax(0,1fr)_14rem] items-center gap-3 py-2 max-md:grid-cols-1"
              >
                <span className="text-sm">{label}</span>
                <NativeSelect
                  aria-label={`${label}: sharing`}
                  className="w-full"
                  value={r.rule}
                  onChange={(e) =>
                    setRows((rs) =>
                      rs.map((x) =>
                        x.subject_kind === r.subject_kind
                          ? { ...x, rule: e.target.value as Sharing["rule"] }
                          : x,
                      ),
                    )
                  }
                >
                  {(Object.keys(RULE_LABELS) as Sharing["rule"][]).map((k) => (
                    <NativeSelectOption key={k} value={k}>
                      {RULE_LABELS[k]}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
              </div>
            );
          })}
        </div>
        <div className="flex items-center gap-3">
          <Button type="submit" size="sm" disabled={!dirty || save.mutation.isPending}>
            {save.mutation.isPending ? "Saving…" : "Save sharing"}
          </Button>
          <SaveIndicator savedAt={save.savedAt} />
        </div>
        <ErrorLine error={save.mutation.error} fallback="Sharing could not be saved." />
      </form>
    </SettingsSection>
  );
}

function InterviewSection({ api, co, view }: SectionProps) {
  const { questions, complete } = view.interview;
  const [answers, setAnswers] = useState<Record<string, string>>(() =>
    Object.fromEntries(questions.map((q) => [q.id, view.interview.answers[q.id] ?? ""])),
  );
  const save = useCrewSave(co, (finish: boolean) =>
    api.client.crews.answerOnboarding({ params: { co }, body: { answers, complete: finish } }),
  );
  const anything = Object.values(answers).some((a) => a.trim());
  const answered = Object.values(answers).filter((a) => a.trim()).length;
  return (
    <SettingsSection
      id="interview"
      icon={MessageSquareTextIcon}
      title="Interview"
      actions={
        <Badge variant={complete ? "success" : "neutral"}>
          {complete ? "Complete" : `${answered} of ${questions.length} answered`}
        </Badge>
      }
    >
      {complete ? (
        <Notice tone="success" data-slot="interview-complete">
          Interview complete: your answers are in your agent's context. Change an answer and save
          and finish again to update it.
        </Notice>
      ) : null}
      <form
        aria-label="Interview"
        className="flex flex-col gap-5"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutation.mutate(false);
        }}
      >
        {questions.map((q) => (
          <Field key={q.id}>
            <FieldLabel htmlFor={`interview-${q.id}`}>{q.prompt}</FieldLabel>
            <Textarea
              id={`interview-${q.id}`}
              rows={q.verbatim ? 5 : 3}
              maxLength={10_000}
              value={answers[q.id] ?? ""}
              aria-describedby={`interview-${q.id}-hint`}
              onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))}
            />
            <FieldDescription id={`interview-${q.id}-hint`}>{q.hint}</FieldDescription>
          </Field>
        ))}
        <div className="flex flex-wrap items-center gap-3">
          <Button type="submit" size="sm" variant="secondary" disabled={save.mutation.isPending}>
            Save answers
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={!anything || save.mutation.isPending}
            onClick={() => save.mutation.mutate(true)}
          >
            Save and finish
          </Button>
          <SaveIndicator savedAt={save.savedAt} />
        </div>
        <ErrorLine error={save.mutation.error} fallback="The answers could not be saved." />
      </form>
    </SettingsSection>
  );
}

function ContextSection({ view }: { view: CrewView }) {
  const layers = view.context_layers;
  return (
    <SettingsSection id="context" icon={BookOpenIcon} title="Context">
      {layers.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Nothing yet: finish the interview to give your agent its first context.
        </p>
      ) : (
        <ul className="flex flex-col gap-4" data-slot="context-layers">
          {layers.map((l) => (
            <li key={l.id} data-layer={l.layer} className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold">{capitalise(l.layer)}</h3>
                <Badge variant="outline">
                  {l.visibility === "owner_only" ? "Only you" : capitalise(l.visibility)}
                </Badge>
                <span className="text-xs text-muted-foreground">
                  as of {new Date(l.as_of).toLocaleDateString()}
                </span>
              </div>
              <p className="whitespace-pre-wrap text-sm text-muted-foreground">{l.body}</p>
            </li>
          ))}
        </ul>
      )}
    </SettingsSection>
  );
}
